<template>
  <FilterOverview :showFilterInitially="false" title="Restriktionen" @reload="onReload">
    <template #title-right>
      <span id="restriktionen-info-target" class="ml-4 d-inline-flex">
        <Info :size="24" color="#B5B5C3" />
      </span>
      <b-tooltip target="restriktionen-info-target" triggers="hover" customClass="restriktionen-info-tooltip">
        <RestriktionenLegende
          hinweis="Doppelklick auf einen Tag: neue Sperrzeit hinzufügen oder eine bestehende Sperrzeit an diesem Tag bearbeiten."
        />
      </b-tooltip>
    </template>

    <template #toolbar-left>
      <div class="d-flex align-items-center flex-wrap">
        <!-- Navigation der Perioden -->
        <div class="restriktionen-nav d-flex align-items-center mr-4">
          <button type="button" class="restriktionen-nav__btn" @click="goPreviousPeriode">
            <ChevronLeft :size="18" />
          </button>
          <span class="restriktionen-nav__label">{{ periodLabel }}</span>
          <button type="button" class="restriktionen-nav__btn" @click="goNextPeriode">
            <ChevronRight :size="18" />
          </button>
        </div>

        <!-- Umschalter zwischen Monats- und Jahresansicht -->
        <div class="restriktionen-toggle mr-4">
          <button
            type="button"
            class="restriktionen-toggle__option"
            :class="{ 'restriktionen-toggle__option--active': viewMode === 'month' }"
            @click="setViewMode('month')"
          >
            Monat
          </button>
          <button
            type="button"
            class="restriktionen-toggle__option"
            :class="{ 'restriktionen-toggle__option--active': viewMode === 'year' }"
            @click="setViewMode('year')"
          >
            Jahr
          </button>
        </div>

        <!-- Filter für Land und Reise, Mehrfachauswahl -->
        <AppSelect
          label="Land"
          class="restriktionen-toolbar__filter mr-4"
          multiple
          apply-mode="button"
          sort="selected-first"
          :options="landOptions"
          :value="landFilter"
          @input="landFilter = $event"
          @apply="loadRestriktionen"
        />

        <AppSelect
          label="Reise"
          class="restriktionen-toolbar__filter mr-4"
          multiple
          apply-mode="button"
          sort="selected-first"
          :options="reiseOptions"
          :value="reiseFilter"
          @input="reiseFilter = $event"
          @apply="onReiseApply"
        />

        <!-- Anzeige der gesperrten Tage -->
        <span class="restriktionen-toolbar__blocked mr-4">
          <CalendarX :size="14" class="mr-2" color="#B5B5C3" />
          <strong>{{ blockedDaysCount }}</strong>
          <span v-if="viewMode === 'month'" class="text-muted">/{{ daysInMonth }}</span>
          <span class="ml-2">geblockte Tage</span>
        </span>

        <!-- Filter und Ansicht zurücksetzen, nur sichtbar wenn es etwas zurückzusetzen gibt -->
        <div
          v-if="canReset"
          class="icon-button d-flex justify-content-center text-danger mr-4"
          style="width: 36px; min-width: 36px"
          @click="onReset"
          v-b-tooltip.hover.bottom="{ customClass: 'tooltip-opacity' }"
          title="Filter zurücksetzen"
        >
          <span class="d-flex align-items-center"><FunnelX :size="20" /></span>
        </div>
      </div>
    </template>

    <template #toolbar-right>
      <b-button
        size="sm"
        class="mr-2 icon-button"
        v-b-tooltip.hover.bottom="{ customClass: 'tooltip-opacity' }"
        title="Sperrzeit hinzufügen"
        @click="openSperrzeitModal(null)"
      >
        <CalendarPlus :size="18" />
      </b-button>
      <b-button
        v-if="restriktionen.length"
        size="sm"
        class="icon-button"
        v-b-tooltip.hover.bottom="{ customClass: 'tooltip-opacity' }"
        title="Excel-Export"
        @click="$bvModal.show('restriktionenExportModal')"
      >
        <Download :size="18" />
      </b-button>
    </template>

    <template #table="{ tableHeight }">
      <div class="restriktionen-content" :style="{ height: tableHeight + 87 + 'px' }">
        <RestriktionenMonthView
          v-if="viewMode === 'month'"
          :date="currentDate"
          :restriktionen="restriktionen"
          @day-doubleclick="onDayDoubleClick"
          @day-add="onDayAdd"
        />
        <RestriktionenYearView
          v-else
          :year="currentYear"
          :restriktionen="restriktionen"
          @month-click="onMonthClick"
        />
      </div>

      <!-- Tages-Detail-Modal, geöffnet per Doppelklick auf einen gesperrten Tag -->
      <RestriktionenKalendertagModal
        :date="selectedDay"
        :entries="selectedDayEntries"
        @edit="onEditRestriktion"
        @delete-tag="onDeleteTag"
        @delete-serie="onDeleteSerie"
        @edit-day="onEditDay"
      />

      <!-- Dialog zum Anlegen und Bearbeiten einer Sperrzeit -->
      <RestriktionenSperrzeitModal
        :prefill-date="sperrzeitPrefillDate"
        :edit-entry="sperrzeitEditEntry"
        :land-options="alleLandOptions"
        :reise-options="alleReiseOptions"
        @created="loadRestriktionen"
      />

      <!-- Dialog zur Zeitraum-Auswahl für den Excel-Export -->
      <RestriktionenExportModal
        :current-date="currentDate"
        :view-mode="viewMode"
        :laender="landFilter"
        :reisen="reiseFilter"
      />

      <!-- Bestätigungsdialog für das Löschen eines Tages oder einer kompletten Serie -->
      <DeleteConfirmationModal
        id="restriktionLoeschenModal"
        :title="deleteTitle"
        :loading="isDeleting"
        :show-irreversible-hint="deleteMode === 'tag'"
        @confirm="confirmDelete"
        @cancel="onDeleteCancel"
      >
        <span v-html="deleteMessage"></span>
      </DeleteConfirmationModal>
    </template>
  </FilterOverview>
</template>

<script>
import moment from 'moment-timezone';
import { Info, ChevronLeft, ChevronRight, FunnelX, CalendarPlus, Download, CalendarX } from 'lucide-vue';
import { mapGetters } from 'vuex';
import FilterOverview from '@/components/common/filter-overview.vue';
import RestriktionenMonthView from '@/components/restriktionen/restriktionen-month-view.vue';
import RestriktionenYearView from '@/components/restriktionen/restriktionen-year-view.vue';
import AppSelect from '@/components/common/app-select/app-select.vue';
import RestriktionenKalendertagModal from '@/components/restriktionen/restriktionen-kalendertag-modal.vue';
import RestriktionenSperrzeitModal from '@/components/restriktionen/restriktionen-sperrzeit-modal.vue';
import RestriktionenExportModal from '@/components/restriktionen/restriktionen-export-modal.vue';
import DeleteConfirmationModal from '@/components/common/delete-confirmation-modal.vue';
import {
  monthLabel,
  countBlockedDays,
  mapKonfigurationToRestriktionen,
  formatSerienBeschreibung,
  formatWochentagDatum,
  getRestriktionZeitraum,
  formatZeitraumText,
  getWochenAnzahl,
  DAY_KEY_FORMAT,
} from '@/components/restriktionen/restriktionen-calendar.helper.js';
import { getRestriktionBezeichnung } from '@/components/restriktionen/restriktionen.constants.js';
import RestriktionenLegende from '@/components/restriktionen/restriktionen-legende.vue';
import {
  getRestriktionskonfiguration,
  deleteRestriktionTag,
  deleteRestriktionSerie,
} from '@/core/restriktionen/services/restriktionskonfiguration.service.js';
import { alleLaender, germanCountryName, reiseCountryToIso3166 } from '@/core/common/iso3166Countries.js';
import { GET_REISEN_KUERZEL_FILTER_OPTIONS } from '@/core/produkte/reisen/stores/reisen.module';

export default {
  name: 'RestriktionenOverviewComponent',
  components: {
    FilterOverview,
    RestriktionenLegende,
    RestriktionenMonthView,
    RestriktionenYearView,
    AppSelect,
    RestriktionenKalendertagModal,
    RestriktionenSperrzeitModal,
    RestriktionenExportModal,
    DeleteConfirmationModal,
    Info,
    ChevronLeft,
    ChevronRight,
    FunnelX,
    CalendarPlus,
    Download,
    CalendarX,
  },
  data() {
    return {
      viewMode: 'month',
      currentDate: moment(),
      landFilter: [],
      reiseFilter: [],
      restriktionen: [],
      isLoading: false,
      // Controller zum Abbrechen eines noch laufenden Requests, wenn ein neuer angestoßen wird
      abortController: null,
      selectedDay: null,
      selectedDayEntries: [],
      sperrzeitPrefillDate: null,
      sperrzeitEditEntry: null,
      restriktionToDelete: null,
      deleteMode: null,
      isDeleting: false,
    };
  },
  computed: {
    ...mapGetters(['getReisekuerzelOptions']),
    // Grundlage beider Filter: alle Reisen aus dem Store mit den ISO-Codes ihrer Länder
    // Zu einer Reise gehören das Hauptland und die weiteren Länder
    reisenMitLand() {
      return (this.getReisekuerzelOptions || [])
        .filter(reise => reise.reisekuerzel)
        .map(reise => ({
          reisekuerzel: reise.reisekuerzel,
          codes: [
            reise.countryIso3166 || reiseCountryToIso3166(reise.country),
            ...(reise.weitereLaender || []).map(this.landnameZuIsoCode),
          ].filter(Boolean),
          country: reise.country,
        }));
    },
    // Ein Kürzel kann in mehreren Reise-Datensätzen und damit in mehreren Ländern vorkommen,
    // deshalb wird pro Kürzel ein Set von ISO-Codes gesammelt und kein einzelner Code
    laenderJeReisekuerzel() {
      return this.reisenMitLand.reduce((map, reise) => {
        if (!reise.codes.length) {
          return map;
        }
        const codes = map.get(reise.reisekuerzel) || new Set();
        reise.codes.forEach(code => codes.add(code));
        return map.set(reise.reisekuerzel, codes);
      }, new Map());
    },
    // Alle Länder, zu denen es überhaupt Reisen gibt, ohne Einschränkung durch den Reise-Filter
    // die alphabetische Sortierung übernimmt der Multiselect
    alleLandOptions() {
      const codes = [...new Set(this.reisenMitLand.flatMap(reise => reise.codes))];
      return codes.map(value => ({ value, text: germanCountryName(value) }));
    },
    // Alle Reisekürzel ohne Einschränkung durch den Land-Filter, Dubletten werden entfernt,
    alleReiseOptions() {
      const kuerzel = [...new Set(this.reisenMitLand.map(reise => reise.reisekuerzel))];
      return kuerzel.map(value => ({ value, text: value }));
    },
    // Optionen des Land-Filters in der Toolbar
    landOptions() {
      if (!this.reiseFilter.length) {
        return this.alleLandOptions;
      }
      const codes = new Set();
      this.reiseFilter.forEach(kuerzel => {
        (this.laenderJeReisekuerzel.get(kuerzel) || []).forEach(code => codes.add(code));
      });
      return this.alleLandOptions.filter(option => codes.has(option.value));
    },
    // Optionen des Reise-Filters in der Toolbar
    reiseOptions() {
      if (!this.landFilter.length) {
        return this.alleReiseOptions;
      }
      const codes = new Set(this.landFilter);
      return this.alleReiseOptions.filter(option => {
        const laender = this.laenderJeReisekuerzel.get(option.value);
        return laender ? [...laender].some(code => codes.has(code)) : false;
      });
    },
    canReset() {
      return (
        this.landFilter.length > 0 ||
        this.reiseFilter.length > 0 ||
        !this.currentDate.isSame(moment(), 'month')
      );
    },
    currentYear() {
      return this.currentDate.year();
    },
    periodLabel() {
      return this.viewMode === 'month' ? monthLabel(this.currentDate) : String(this.currentYear);
    },
    daysInMonth() {
      return this.currentDate.daysInMonth();
    },
    blockedDaysCount() {
      return countBlockedDays(this.restriktionen, this.currentDate, this.viewMode);
    },
    deleteTitle() {
      return this.deleteMode === 'tag' ? 'Tag löschen?' : 'Sperrzeit löschen?';
    },
    deleteMessage() {
      const entry = this.restriktionToDelete;
      if (!entry) {
        return '';
      }
      const bezeichnung = getRestriktionBezeichnung(entry);
      // Grund und Datumsangaben werden im Text hervorgehoben (fett)
      const grund = `<strong>${entry.grund}</strong>`;
      // Einzelner Tag (nur bei wiederkehrenden Serien): der Tag wird aus der Serie entfernt, der Rest bleibt
      if (this.deleteMode === 'tag') {
        const tag = formatWochentagDatum(entry.date);
        const beschreibung = formatSerienBeschreibung(entry);
        const serie = beschreibung
          ? `aus der wiederkehrenden Serie „<strong>${beschreibung}</strong>"`
          : 'aus der wiederkehrenden Serie';
        const { start, ende } = getRestriktionZeitraum(entry);
        const zeitraum = ende
          ? `${start.format('DD.MM.YYYY')} – ${ende.format('DD.MM.YYYY')}`
          : `ab ${start.format('DD.MM.YYYY')}`;
        return (
          `Soll die Sperrzeit am <strong>${tag}</strong> ${serie} entfernt werden?` +
          `<br /><br />Die übrige Serie (<strong>${zeitraum}</strong>) bleibt bestehen.`
        );
      }
      // Komplette Sperrzeit: der Zeitraum nennt je nach Art den einen Tag, die Spanne oder den offenen Beginn
      const zeitraum = formatZeitraumText(entry, { betonen: true });
      return `Soll die ${bezeichnung} ${grund} ${zeitraum} wirklich gelöscht werden?`;
    },
  },
  watch: {
    currentDate() {
      this.loadRestriktionen();
    },
    viewMode() {
      this.loadRestriktionen();
    },
  },
  created() {
    this.$store.dispatch(GET_REISEN_KUERZEL_FILTER_OPTIONS);
  },
  beforeDestroy() {
    if (this.abortController) {
      this.abortController.abort();
    }
  },
  methods: {
    // Entfernt eine Auswahl, die durch die gegenseitige Filterung nicht mehr zu den Optionen passt
    // Der Land-Filter wird zuerst bereinigt, damit die Reise-Optionen auf der bereinigten
    // Länderauswahl beruhen
    entferneUngueltigeAuswahl() {
      this.landFilter = this.nurGueltigeAuswahl(this.landFilter, this.landOptions);
      this.reiseFilter = this.nurGueltigeAuswahl(this.reiseFilter, this.reiseOptions);
    },
    // Reduziert eine Auswahl auf die Einträge, die noch in den Optionen enthalten sind
    // Bei unveränderter Auswahl wird dieselbe Referenz zurückgegeben, damit keine Neuberechnung anläuft
    nurGueltigeAuswahl(auswahl, optionen) {
      const gueltigeWerte = new Set(optionen.map(option => option.value));
      const gefiltert = auswahl.filter(wert => gueltigeWerte.has(wert));
      return gefiltert.length === auswahl.length ? auswahl : gefiltert;
    },
    // Ländername oder ISO-Code rein, immer ISO-Code raus, unbekannte Einträge entfallen
    landnameZuIsoCode(eintrag) {
      return alleLaender()[eintrag] ? eintrag : reiseCountryToIso3166(eintrag);
    },
    // Beim Anwenden des Reise-Filters werden Hauptland und weitere Länder der Reisen mitausgewählt
    onReiseApply() {
      const codes = new Set(this.landFilter);
      this.reiseFilter.forEach(kuerzel => {
        (this.laenderJeReisekuerzel.get(kuerzel) || []).forEach(code => codes.add(code));
      });
      this.landFilter = [...codes];
      this.loadRestriktionen();
    },
    setViewMode(mode) {
      this.viewMode = mode;
    },
    goPreviousPeriode() {
      this.currentDate = this.currentDate.clone().subtract(1, this.viewMode === 'month' ? 'month' : 'year');
    },
    goNextPeriode() {
      this.currentDate = this.currentDate.clone().add(1, this.viewMode === 'month' ? 'month' : 'year');
    },
    onMonthClick(date) {
      this.currentDate = moment(date);
      this.viewMode = 'month';
    },
    onDayDoubleClick({ date, entries }) {
      this.selectedDay = date;
      this.selectedDayEntries = entries;
      this.$bvModal.show('restriktionenKalendertagModal');
    },
    onEditRestriktion(entry) {
      this.$bvModal.hide('restriktionenKalendertagModal');
      this.sperrzeitPrefillDate = null;
      this.sperrzeitEditEntry = entry;
      this.$nextTick(() => this.$bvModal.show('restriktionenSperrzeitModal'));
    },
    onDeleteTag(entry) {
      this.openDeleteDialog(entry, 'tag');
    },
    onDeleteSerie(entry) {
      this.openDeleteDialog(entry, 'serie');
    },
    openDeleteDialog(entry, mode) {
      this.restriktionToDelete = entry;
      this.deleteMode = mode;
      this.$bvModal.hide('restriktionenKalendertagModal');
      this.$bvModal.show('restriktionLoeschenModal');
    },
    onDeleteCancel() {
      this.$bvModal.show('restriktionenKalendertagModal');
    },
    async confirmDelete() {
      const entry = this.restriktionToDelete;
      if (!entry) {
        return;
      }
      this.isDeleting = true;
      try {
        if (this.deleteMode === 'serie') {
          await deleteRestriktionSerie({ id: entry.id });
        } else {
          await deleteRestriktionTag({ id: entry.id, datum: entry.dateIso });
        }
        this.$bvModal.hide('restriktionLoeschenModal');
        this.loadRestriktionen();
      } catch (error) {
        // Fehler wurden bereits im Service behandelt, Dialog bleibt zur erneuten Aktion offen
      } finally {
        this.isDeleting = false;
      }
    },
    openSperrzeitModal(dateKey) {
      this.sperrzeitPrefillDate = dateKey;
      this.sperrzeitEditEntry = null;
      this.$nextTick(() => this.$bvModal.show('restriktionenSperrzeitModal'));
    },
    onDayAdd({ date }) {
      this.openSperrzeitModal(moment(date).format(DAY_KEY_FORMAT));
    },
    onEditDay() {
      this.$bvModal.hide('restriktionenKalendertagModal');
      this.openSperrzeitModal(this.selectedDay ? moment(this.selectedDay).format(DAY_KEY_FORMAT) : null);
    },
    onReset() {
      if (!this.canReset) {
        return;
      }
      this.landFilter = [];
      this.reiseFilter = [];
      this.currentDate = moment();
      // Nach dem Leeren der Filter werden die Restriktionen sofort geleert, da kein Filter mehr aktiv ist
      this.loadRestriktionen();
    },
    onReload() {
      this.loadRestriktionen();
    },
    async loadRestriktionen() {
      this.entferneUngueltigeAuswahl();
      const laender = this.landFilter;
      const reisen = this.reiseFilter;

      if (laender.length === 0 && reisen.length === 0) {
        this.restriktionen = [];
        return;
      }

      // im Monatsmodus umfasst der Zeitraum das komplette Wochenraster
      // damit tragen auch die ausgegrauten Tage der Nachbarmonate ihre Restriktionen
      const istMonat = this.viewMode === 'month';
      const rasterStart = this.currentDate.clone().startOf('month').startOf('isoWeek');
      const rasterEnde = rasterStart.clone().add(getWochenAnzahl(this.currentDate) * 7 - 1, 'days');
      const von = (istMonat ? rasterStart : this.currentDate.clone().startOf('year')).format(DAY_KEY_FORMAT);
      const bis = (istMonat ? rasterEnde : this.currentDate.clone().endOf('year')).format(DAY_KEY_FORMAT);

      if (this.abortController) {
        this.abortController.abort();
      }
      const controller = new AbortController();
      this.abortController = controller;
      this.isLoading = true;

      try {
        const result = await getRestriktionskonfiguration({
          von,
          bis,
          laender,
          reisen,
          signal: controller.signal,
        });
        // Ergebnis eines zwischenzeitlich abgebrochenen Requests wird verworfen
        if (controller.signal.aborted) {
          return;
        }
        this.restriktionen = mapKonfigurationToRestriktionen(result);
      } catch (error) {
        // Fehler wurden bereits im Service behandelt, hier keine weitere Anzeige
      } finally {
        if (this.abortController === controller) {
          this.isLoading = false;
          this.abortController = null;
        }
      }
    },
  },
};
</script>

<style scoped>
.restriktionen-nav {
  gap: 8px;
}

.restriktionen-nav__btn {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 36px;
  height: var(--control-h);
  padding: 0;
  background: var(--surface);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.05);
  color: var(--text);
  cursor: pointer;
}

.restriktionen-nav__btn:hover {
  background: var(--surface-sunken);
}

.restriktionen-toggle {
  display: inline-flex;
  align-items: center;
  height: var(--control-h);
  padding: 3px;
  background: var(--surface-sunken);
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.05);
}

.restriktionen-toggle__option {
  height: 100%;
  padding: 0 18px;
  border: 1px solid transparent;
  border-radius: var(--radius-sm);
  background: transparent;
  font-size: 14px;
  font-weight: 600;
  color: var(--text-muted);
  cursor: pointer;
  transition: background-color 0.15s ease, color 0.15s ease;
}

.restriktionen-toggle__option--active {
  background: var(--surface);
  color: var(--text);
  border: 1px solid var(--border);
}

.restriktionen-nav__label {
  min-width: 120px;
  font-weight: 600;
  font-size: 14px;
  text-align: center;
  color: var(--text);
}

.restriktionen-toolbar__filter {
  width: 170px;
}

.restriktionen-toolbar__blocked {
  display: inline-flex;
  align-items: center;
  height: var(--control-h);
  padding: 0 12px;
  border: 1px solid var(--border);
  border-radius: var(--radius-sm);
  box-shadow: 0px 1px 4px 0px rgba(0, 0, 0, 0.05);
  font-size: 14px;
  line-height: 1;
  color: var(--text-body);
}

.restriktionen-toolbar__blocked svg {
  display: block;
}

.icon-button {
  cursor: pointer;
}

.restriktionen-content {
  display: flex;
  flex-direction: column;
  width: 100%;
  overflow-y: auto;
}
</style>

<style>
.tooltip-opacity {
  opacity: 1 !important;
}

.restriktionen-info-tooltip {
  opacity: 1 !important;
}

.restriktionen-info-tooltip .tooltip-inner {
  max-width: 360px;
  padding: 16px;
  background: var(--surface);
  text-align: left;
  border-radius: var(--radius-sm);
  box-shadow: var(--shadow-lg);
  border: 1px solid var(--border);
}
</style>
